/*===============================================================================*/
/*********************************************************************************/
/**
 * @fileOverview Runtime /coco-config.json overlay for host-agnostic mycoco.
 * @author Elear Solutions Dev, mail@elear.solutions
 * @copyright Copyright (c) 2026 Elear Solutions Tech Private Limited. All rights
 * reserved.
 * @license To any person (the "Recipient") obtaining a copy of this software and
 *            the associated documentation files (the "Software"):
 *
 * All information contained in or disclosed by this software is confidential
 * and proprietary information of Elear Solutions Tech Private Limited and all
 * rights therein are expressly reserved. By accepting this material the
 * recipient agrees that this material and the information contained therein is
 * held in confidence and in trust and will NOT be used, copied, modified,
 * merged, published, distributed, sublicensed, reproduced in whole or in part,
 * nor its contents revealed in any manner to others without the express
 * written permission of Elear Solutions Tech Private Limited.
 */
/*********************************************************************************/
/*===============================================================================*/

const CONFIG_FETCH_TIMEOUT_MS = 8000;

let cfg = {
  authority: process.env.REACT_APP_BASE_API_PATH,
  redirectUri: process.env.REACT_APP_REDIRECT_URI,
  clientId: process.env.REACT_APP_CLIENT_ID,
  baseHost: process.env.REACT_APP_BASE_URL,
  domainName: undefined,
  applied: false
};

/**
 * Overlay compile-time REACT_APP_* defaults from a parsed coco-config object.
 *
 * @param {Object} json
 * @param {String} json.authority
 * @param {String} [json.redirectUri]
 * @param {String} [json.clientId]
 * @param {String} [json.cmsOrigin]
 * @param {String} [json.domainName]
 * @returns {void}
 */
export function applyRuntimeConfig(json) {
  if (!json || typeof json.authority !== "string") {
    return;
  }
  const authority = json.authority.trim().replace(/\/$/, "");
  if (!authority) {
    return;
  }
  cfg.authority = authority;
  if (typeof json.redirectUri === "string") {
    cfg.redirectUri = json.redirectUri;
  }
  if (typeof json.clientId === "string") {
    cfg.clientId = json.clientId;
  }
  if (typeof json.cmsOrigin === "string") {
    try {
      cfg.baseHost = new URL(json.cmsOrigin).host;
    } catch (_err) {
      cfg.baseHost = json.cmsOrigin
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "");
    }
  }
  if (typeof json.domainName === "string" && json.domainName) {
    cfg.domainName = json.domainName;
  }
  cfg.applied = true;
}

/** @returns {String} API origin (no trailing slash) */
export function getApiUrl() {
  return cfg.authority;
}

/** @returns {String} OAuth client id */
export function getClientId() {
  return cfg.clientId;
}

/** @returns {String} OAuth redirect URI */
export function getRedirectUri() {
  return cfg.redirectUri;
}

/** @returns {String} CMS host without scheme */
export function getBaseHost() {
  return cfg.baseHost;
}

/** @returns {String} https:// + CMS host */
export function getCmsOrigin() {
  return "https://" + cfg.baseHost;
}

/** @returns {String|undefined} cookie-session domain suffix when set */
export function getDomainName() {
  return cfg.domainName;
}

/** @returns {Boolean} true after a valid /coco-config.json apply */
export function runtimeConfigApplied() {
  return cfg.applied;
}

/** @returns {String} oidc-client localStorage key for the current authority */
export function getOidcUserKey() {
  return "oidc.user:" + cfg.authority + ":" + cfg.clientId;
}

/** Test-only: restore compile-time defaults. */
export function resetRuntimeConfigForTests() {
  cfg.authority = process.env.REACT_APP_BASE_API_PATH;
  cfg.redirectUri = process.env.REACT_APP_REDIRECT_URI;
  cfg.clientId = process.env.REACT_APP_CLIENT_ID;
  cfg.baseHost = process.env.REACT_APP_BASE_URL;
  cfg.domainName = undefined;
  cfg.applied = false;
}

/**
 * Fetch /coco-config.json. Miss, non-JSON, or abort keeps compile-time bake.
 * Never rejects.
 *
 * @param {Number} [timeoutMs=8000] abort the fetch after this many ms
 * @returns {Promise<void>}
 */
export async function loadRuntimeConfig(
  timeoutMs = CONFIG_FETCH_TIMEOUT_MS
) {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetch("/coco-config.json", {
      signal: controller.signal
    });
    if (!response.ok) {
      return;
    }
    applyRuntimeConfig(JSON.parse(await response.text()));
  } catch (_err) {
    /* S3 error-doc HTML, abort, or missing file — keep bake */
  } finally {
    clearTimeout(timer);
  }
}
